/**
 * Client-side fetcher for professional-tier plan rows.
 *
 * Hits the local Next.js route ``GET /api/pricing/plans?category=professional``
 * which proxies the backend ``/internal/pc`` endpoint and filters the
 * response to rows where ``plan_type = 'professional'``. The Next.js route
 * also handles the gzip/base64 decoding of the backend payload, so this
 * client just sees plain JSON.
 *
 * Why its own service (not the Redux pricingSlice the /pricing page uses):
 *   - Professional plans are their own display surface (ProfessionalPricingSection)
 *     with a different lifetime — fetched once per dialog open, not once
 *     per app init.
 *   - No need for cross-component sharing yet; a future "professional
 *     pricing" marketing page can promote this to Redux if it lands.
 */

/**
 * Per-locale display copy sourced from ``public.plan_translations``.
 * Forwarded as-is by the Next.js route from /internal/pc.
 */
export interface ProfessionalPlanTranslation {
  display_name: string;
  short_tagline: string | null;
  description: string | null;
  cta_label: { default: string; [audience: string]: string } | null;
  value_proposition: string | null;
}

/**
 * One feature bullet sourced from ``public.plan_features`` +
 * ``public.feature_translations``.
 */
export interface ProfessionalPlanFeature {
  feature_key: string;
  display_order: number;
  is_highlighted: boolean;
  capability_key: string | null;
  /** locale → display text (HTML-permissive: <strong>, <em>) */
  translations: Partial<Record<string, string>>;
}

export interface ProfessionalPlanRow {
  /** DB tag — ``prof_profile`` (standalone Profil+). */
  tag: string;
  plan_name: string;
  price_cents: number;
  price_eur: number;
  is_featured: boolean;
  display_order: number;
  plan_type: string | null;
  annual_price_eur: number | null;
  overflow_rate_eur: number | null;
  stripe_price_id: string | null;
  stripe_annual_price_id: string | null;
  /** Per-locale catalog copy. Empty object when no row is backfilled yet. */
  translations?: Record<string, ProfessionalPlanTranslation>;
  /** Catalog-driven feature bullets, ordered by ``display_order``. */
  features?: ProfessionalPlanFeature[];
}

interface PlansResponse {
  plans: ProfessionalPlanRow[];
  locale: string;
}

/**
 * Fetch the current set of professional-tier plans.
 *
 * Returns an empty array on network or parse failure — callers should
 * render a non-blocking "failed to load plans" message rather than crash
 * the dialog.
 */
export async function fetchProfessionalPlans(): Promise<ProfessionalPlanRow[]> {
  try {
    const response = await fetch('/api/pricing/plans?category=professional', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return [];
    const payload = (await response.json()) as PlansResponse;
    return payload.plans ?? [];
  } catch {
    return [];
  }
}
